import React, { useState } from 'react';
import { ImageIcon } from 'lucide-react';

interface ScreenshotFrameProps {
  // Dark-mode screenshot (required). Path under /public.
  srcDark: string;
  // Light-mode screenshot (optional). If absent or it fails to load, the dark
  // image is shown in light mode as a graceful fallback.
  srcLight?: string;
  alt: string;
  // Optional filename/label shown in the window chrome bar.
  caption?: string;
  // Optional compliance/disclaimer line rendered as a small footer band inside
  // the frame border (so it stays bounded by the image's own width).
  disclaimer?: string;
  className?: string;
}

/**
 * Browser-chrome window frame around a real platform screenshot, theme-aware:
 * shows the dark capture in dark mode and the light capture in light mode
 * (purely via Tailwind `dark:` visibility — follows the user's theme toggle
 * with no JS). If a light capture isn't present yet, light mode falls back to
 * the dark image. If the dark image is missing too, a labeled placeholder shows
 * instead of a broken-image icon.
 */
const ScreenshotFrame: React.FC<ScreenshotFrameProps> = ({
  srcDark,
  srcLight,
  alt,
  caption,
  disclaimer,
  className,
}) => {
  const [darkErr, setDarkErr] = useState(false);
  const [lightErr, setLightErr] = useState(false);
  const usableLight = !!srcLight && !lightErr;

  return (
    <figure
      className={`rounded-2xl bg-white dark:bg-gray-900 shadow-xl ring-1 ring-gray-200/70 dark:ring-gray-800 overflow-hidden ${className ?? ''}`}
    >
      <div className="flex items-center gap-1.5 px-4 py-3 border-b border-gray-100 dark:border-gray-800">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
        <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
        {caption && (
          <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 truncate">{caption}</span>
        )}
      </div>
      {darkErr ? (
        <figcaption className="aspect-[16/10] flex flex-col items-center justify-center gap-2 bg-gray-50 dark:bg-gray-800/40 text-center px-6">
          <ImageIcon className="h-8 w-8 text-gray-300 dark:text-gray-600" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{alt}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Add image at <code className="font-mono">{srcDark}</code>
          </p>
        </figcaption>
      ) : (
        <>
          {/* Dark capture: shown in dark mode; also used in light mode when no
              usable light capture exists. */}
          <img
            src={srcDark}
            alt={alt}
            loading="lazy"
            onError={() => setDarkErr(true)}
            className={`w-full ${usableLight ? 'hidden dark:block' : 'block'}`}
          />
          {/* Light capture: shown only in light mode, only while it loads. */}
          {srcLight && !lightErr && (
            <img
              src={srcLight}
              alt={alt}
              loading="lazy"
              onError={() => setLightErr(true)}
              className="w-full block dark:hidden"
            />
          )}
        </>
      )}
      {disclaimer && (
        <p className="border-t border-gray-100 dark:border-gray-800 px-4 py-2 text-[0.6rem] leading-snug text-gray-400 dark:text-gray-500 text-center">
          {disclaimer}
        </p>
      )}
    </figure>
  );
};

export default ScreenshotFrame;
